/**
 * @typedef { import('./EventWebinarDetail').EventWebinarDetail } EventWebinarDetail
 */

import { useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useLocalization } from '../../custom-hooks/useLocalization';
import { compose } from '../../utils/compose';
import { withStrictMode } from '../../utils/with-strict-mode';
import { withMobileOnlyStyles } from '../../utils/breakpoints';
import { device } from '../../../utils/styled-component-mixins';
import { withLocalization } from '../../shared/Context/LocalizationContext';
import { withServerStyles } from '../../shared/ServerStylesConditionalWrapper';
import Accordion from '../../shared/Accordion';
import { useEventWebinarContext, withEventWebinarContext } from './EventWebinarContext';
import { CpeCredits, CpeInformation } from './CpeSections';
import DesktopSidebar from './DesktopSidebar';
import EventDescription from './EventDescription';
import DetailsAndRegistrationCard from './DetailsAndRegistrationCard';
import ContactAndConnect from './ContactAndConnect';
import Presenter from './Presenter';
import EventsWebinarsSliderComponent from '../EventsWebinarsSliderComponent';

const ContainerStyles = createGlobalStyle`
  .event-webinar-detail {
    padding-block: 4rem;
  }

  .accordion-item {
    margin-inline: -1.5rem; // Escape container padding

    @media ${device.desktop} {
      margin-inline: 0;
    }

    h3 {
      font-size: 2rem;
      line-height: 3.2rem;
    }

    @media ${device.desktop} {
      h3 {
        font-size: 2.2rem;
      }
    }
  }

  .slider .heading-wrapper {
    margin-bottom: 4rem;
  }
`;

const StyledSection = styled.section`
  @media ${device.desktop} {
    --sidebar-width: 26rem;
    --gap: 6rem;

    display: grid;
    grid: 1fr / minmax(0, 1fr) var(--sidebar-width);
    gap: var(--gap);
  }

  @media ${device.desktopLg} {
    --sidebar-width: 34rem;
    --gap: 13rem;
  }

  .slider {
    grid-column: -1 / 1;

    margin-inline: -1.5rem;

    @media ${device.desktop} {
      margin-inline: -7rem;
    }
  }

  article {
    margin-bottom: 8rem;

    @media ${device.desktop} {
      margin-bottom: 0;
    }
  }
`;

const Title = styled.h1`
  margin-block: 6rem 4rem;

  @media ${device.deviceOnly} {
    margin-top: 4rem;
  }
`;

const StyledMobileDetailsAndRegistrationCard = withMobileOnlyStyles(DetailsAndRegistrationCard);
const StyledMobileContactAndConnect = styled(withMobileOnlyStyles(ContactAndConnect))`
  margin-block: 8rem;
`;

const StyledPresentersWrapper = styled.div`
  padding-inline: 0 !important; // Overrides accordion styles

  @media ${device.desktop} {
    display: grid;
    gap: 1.6rem;
  }
`;

/**
 * @param {EventWebinarDetail}
 * @returns {ReactElement}
 */
const EventWebinarDetail = () => {
  const { resourceStrings } = useLocalization();
  const {
    eventType,
    title,
    cpeCount,
    presenters,
    eventModerators,
    showArchivedWebinar,
    hideSocialShare,
    accordionContent,
    relatedEvents,
    disclaimer
  } = useEventWebinarContext();

  const eventTypeDisplayName = useMemo(() => {
    switch (eventType) {
      case 0:
      default:
        return resourceStrings['bdO.EventsWebinars.Event'];
      case 1:
        return resourceStrings['bdO.EventsWebinars.Webinar'];
      case 2:
        return resourceStrings['bdO.EventsWebinars.PastWebinar'];
    }
  }, [eventType, resourceStrings]);

  return (
    <StyledSection>
      <article>
        <ContainerStyles />
        <Title>{title}</Title>
        <StyledMobileDetailsAndRegistrationCard eventTypeDisplayName={eventTypeDisplayName} />
        {!!cpeCount && <CpeCredits />}
        <EventDescription />
        {!!cpeCount && <CpeInformation />}
        {!!presenters.length && (
          <Accordion
            className="accordion-item"
            pageTitle={resourceStrings['bdO.EventsWebinars.Presenters']}
            index={1}
          >
            <StyledPresentersWrapper>
              {presenters.map((presenter) => (
                <Presenter {...presenter} key={presenter.presenterGuid} />
              ))}
            </StyledPresentersWrapper>
          </Accordion>
        )}
        {!!eventModerators.length && (
          <Accordion className="accordion-item" pageTitle="Moderator" index={2}>
            <StyledPresentersWrapper>
              {eventModerators.map((moderator) => (
                <Presenter {...moderator} key={moderator.moderatorGuid} />
              ))}
            </StyledPresentersWrapper>
          </Accordion>
        )}
        {showArchivedWebinar && accordionContent && (
          <Accordion
            className="accordion-item"
            pageTitle={resourceStrings['bdO.EventsWebinars.RecordingAndMaterials']}
            index={3}
          >
            <div dangerouslySetInnerHTML={{ __html: accordionContent }} />
          </Accordion>
        )}
        <div dangerouslySetInnerHTML={{ __html: disclaimer }} />
      </article>
      <StyledMobileContactAndConnect eventTypeDisplayName={eventTypeDisplayName} />
      <DesktopSidebar
        eventTypeDisplayName={eventTypeDisplayName}
        hideSocialShare={hideSocialShare}
      />
      {relatedEvents.eventsWebinars.length > 0 && (
        <div className="slider">
          <EventsWebinarsSliderComponent {...relatedEvents} hasFilter={false} />
        </div>
      )}
    </StyledSection>
  );
};

export default compose(
  withStrictMode,
  withLocalization,
  withServerStyles,
  withEventWebinarContext
)(EventWebinarDetail);
